import React, { useState, useEffect, useMemo } from 'react';
import DataTable from '../components/common/DataTable';
import { DataForm } from '../components/common/DataForm';
import SearchBar from '../components/common/SearchBar';
import PageHeader from '../components/common/PageHeader';
import Pagination from '../components/common/Pagination';
import DeleteConfirmModal from '../components/common/DeleteConfirmModal';
import useDebouncedSearch from '../hooks/useDebouncedSearch';
import { KeyRound, CheckCircle, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { PermissionsService } from '../services/permissions.service';

const COLUMNS = [
  { header: 'Permission Name', accessor: 'name' },
  { header: 'Display Name', accessor: 'display_name' },
  { header: 'Module', accessor: 'module' },
  { header: 'Action', accessor: 'action' },
  { header: 'Status', accessor: 'status' },
];

const MODULE_OPTIONS = [
  { label: 'Dashboard', value: 'dashboard' },
  { label: 'Inquiries', value: 'inquiries' },
  { label: 'Tournaments', value: 'tournaments' },
  { label: 'Players', value: 'players' },
  { label: 'Committee', value: 'committee' },
  { label: 'News & Media', value: 'news' },
  { label: 'About', value: 'about' },
  { label: 'Gallery', value: 'gallery' },
  { label: 'Roles', value: 'roles' },
  { label: 'Permissions', value: 'permissions' },
];

const ACTION_OPTIONS = [
  { label: 'Create', value: 'create' },
  { label: 'Read', value: 'read' },
  { label: 'Update', value: 'update' },
  { label: 'Delete', value: 'delete' },
  { label: 'Manage (All)', value: 'manage' },
];

const FORM_FIELDS = [
  { label: 'Permission Name', accessor: 'name', placeholder: 'e.g. players:create' },
  { label: 'Display Name', accessor: 'display_name', placeholder: 'e.g. Create Players' },
  {
    label: 'Module', accessor: 'module', type: 'select', options: MODULE_OPTIONS,
  },
  {
    label: 'Action', accessor: 'action', type: 'select', options: ACTION_OPTIONS,
  },
  {
    label: 'Status', accessor: 'status', type: 'select', options: [
      { label: 'Active', value: 'active' },
      { label: 'Inactive', value: 'inactive' },
    ]
  },
  {
    label: 'Description',
    accessor: 'description',
    type: 'textarea',
    placeholder: 'Describe what this permission allows...',
    fullWidth: true,
  },
];

export default function Permissions() {
  const [dataList, setDataList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingData, setEditingData] = useState(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const { searchQuery, setSearchQuery, debouncedQuery } = useDebouncedSearch(() => setCurrentPage(1));
  const itemsPerPage = 8;

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await PermissionsService.getAllPermissions();
      const list = Array.isArray(res) ? res : (res?.data || []);
      const formatted = list.map(item => ({ ...item, id: item._id || item.id }));
      setDataList(formatted);
    } catch (error) {
      toast.error('Failed to load permissions');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const filteredData = useMemo(() => {
    if (!debouncedQuery) return dataList;
    return dataList.filter(item =>
      Object.values(item).some(val =>
        String(val).toLowerCase().includes(debouncedQuery.toLowerCase())
      )
    );
  }, [dataList, debouncedQuery]);

  const kpis = useMemo(() => {
    const total = dataList.length;
    const active = dataList.filter(d => d.status === 'active').length;
    const modules = new Set(dataList.map(d => d.module).filter(Boolean)).size;
    return [
      { label: 'Total Permissions', value: total, icon: <KeyRound size={20} />, color: 'blue' },
      { label: 'Active', value: active, icon: <CheckCircle size={20} />, color: 'green' },
      { label: 'Modules Covered', value: modules, icon: <XCircle size={20} />, color: 'orange' },
    ];
  }, [dataList]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleFormSubmit = async (formData) => {
    try {
      if (editingData) {
        const targetId = editingData._id || editingData.id;
        await PermissionsService.updatePermission(targetId, formData);
        toast.success('Permission updated successfully!');
      } else {
        await PermissionsService.createPermission(formData);
        toast.success('Permission created successfully!');
      }
      await fetchData();
      setIsFormOpen(false);
      setEditingData(null);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save permission');
    }
  };

  const handleEdit = (row) => {
    setEditingData(row);
    setIsFormOpen(true);
  };

  const handleDeleteClick = (row) => {
    setDeleteTarget(row);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteExecute = async () => {
    if (!deleteTarget) return;
    try {
      const targetId = deleteTarget._id || deleteTarget.id;
      await PermissionsService.deletePermission(targetId);
      setDataList(prev => prev.filter(item => item.id !== targetId));
      toast.success('Permission deleted successfully!');
    } catch (error) {
      toast.error('Failed to delete permission');
    } finally {
      setIsDeleteModalOpen(false);
      setDeleteTarget(null);
    }
  };

  return (
    <div className="page-content">
      <PageHeader
        title="Permissions"
        subtitle="Define granular access permissions for each module"
        onAdd={() => { setEditingData(null); setIsFormOpen(true); }}
        addLabel="Add Permission"
        kpis={kpis}
      />

      {isFormOpen && (
        <DataForm
          title={editingData ? 'Edit Permission' : 'Add New Permission'}
          fields={FORM_FIELDS}
          initialData={editingData}
          onSubmit={handleFormSubmit}
          onClose={() => setIsFormOpen(false)}
        />
      )}

      <div className="table-card-container">
        <div className="table-actions-area">
          <div className="table-actions-left">
            <SearchBar
              placeholder="Search permissions..."
              value={searchQuery}
              onChange={setSearchQuery}
            />
          </div>
        </div>

        <div className="table-responsive-wrapper">
          <DataTable
            columns={COLUMNS}
            data={paginatedData}
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
            loading={isLoading}
          />
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalEntries={filteredData.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
        />
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setDeleteTarget(null); }}
        onConfirm={handleDeleteExecute}
        title="Delete Permission"
        message="Are you sure you want to delete this permission? It may be in use by existing roles."
        itemName={deleteTarget ? deleteTarget.display_name || deleteTarget.name : ''}
      />
    </div>
  );
}
